/**
 * Shared helpers for the group analysis page (chart + ledger).
 *
 * Difficulty buckets are computed by the API (single source of truth
 * for thresholds); this module only maps them to tones and resolves
 * which score a task shows under the current model filter.
 */

import type { AnalysisTask, AnalysisTaskDifficulty } from '@/api/generated/model'

/**
 * Difficulty tones from the eval charts' fixed brand-family palette
 * (see SERIES_COLORS in ScoreHistoryChart) — literal-color exception:
 * semantic tokens only offer one chart color (VISUAL_DESIGN_PRINCIPLES §3).
 */
export const DIFFICULTY_TONES: Record<AnalysisTaskDifficulty, string> = {
  easy: 'hsl(126 12% 42%)', // sage
  medium: 'hsl(24 30% 45%)', // clay
  hard: 'hsl(0 35% 45%)', // muted red
  unscored: 'hsl(var(--muted-foreground) / 0.5)',
}

export const DIFFICULTY_LABELS: Record<AnalysisTaskDifficulty, string> = {
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
  unscored: 'Unscored',
}

/**
 * Continuous score → tone ramp for chart lines: hue rotates red (0) →
 * green (score 1), same convention as VariantMatrix's cell tint but at
 * line-weight saturation. Muted per VISUAL_DESIGN_PRINCIPLES §3.
 */
export function scoreTone(score: number): string {
  // Console ramp: green → yellow → orange → pink-red. Piecewise hue so
  // the midpoint lands on ORANGE (35°) instead of muddy olive, with
  // saturation/lightness rising toward the red end.
  const hue =
    score >= 0.5
      ? 35 + (score - 0.5) * 2 * (140 - 35) // orange → green
      : (350 + score * 2 * 45) % 360 // pink-red → orange
  // Pastel: softer saturation, lifted lightness.
  const saturation = 45 + (1 - score) * 15
  const lightness = 52 + (1 - score) * 4
  return `hsl(${hue.toFixed(0)} ${saturation.toFixed(0)}% ${lightness.toFixed(0)}%)`
}

/**
 * Pass/fail verdict tints (rubric rows, grade badges) — brighter than
 * the difficulty tones so the verdict reads at a glance.
 */
export const VERDICT_TONES = {
  pass: 'hsl(126 55% 36%)',
  fail: 'hsl(0 55% 46%)',
} as const

/** Faint background wash matching the verdict tone. */
export const VERDICT_FILLS = {
  pass: 'hsl(126 55% 36% / 0.07)',
  fail: 'hsl(0 55% 46% / 0.07)',
} as const

/**
 * The score a task shows under the model filter: the selected model's
 * latest score, or the task headline when no model is selected. Null
 * means the task drops out of the chart (but stays in the ledger).
 */
export function taskScoreFor(task: AnalysisTask, model: string | null): number | null {
  if (model === null) return task.score
  const entry = task.model_scores.find((score) => score.model_slug === model)
  return entry ? entry.score : null
}

/** The `?model=` search param, or null for "all models". */
export function selectedModel(searchParams: URLSearchParams): string | null {
  return searchParams.get('model')
}
